/**
 * Graphique d'évolution du poids — brut et lissé superposés (specs §6.6).
 *
 * 🔴 **CHOIX : SVG écrit à la main, aucune bibliothèque de graphique.**
 * L'architecture n'en retient aucune (§13 des specs laissait la question
 * ouverte, l'archi ne la tranche pas). Le précédent du projet est celui des
 * glyphes d'onglets de l'étape 5 : « quatre glyphes ne valent pas une dépendance
 * de plus ». Ici c'est encore plus net —
 *
 *   - Recharts, Chart.js ou visx pèsent de 40 à 120 ko compressés, soit entre un
 *     tiers et la totalité du bundle actuel (123 ko à l'étape 6), pour **un seul
 *     graphique dans toute l'application**.
 *   - Aucune ne sait dessiner ce qu'on veut sans configuration : deux séries de
 *     nature différente (une mesure et son lissage), une ligne de référence, un
 *     axe temporel à trous. On écrirait autant de configuration que de SVG.
 *   - Le budget est à 0 € et la stack « légère obligatoire » (specs §2) ; le VPS
 *     construit le front lui-même (§7.6).
 *
 * Ce fichier fait ~250 lignes de géométrie explicite et n'ajoute **aucune**
 * dépendance. Si un jour la V2 demande dix graphiques (specs §12.5), la question
 * se reposera — et ce fichier sera le cahier des charges de la bibliothèque à
 * choisir.
 *
 * ⚠️ **Fond uni, aucun `backdrop-filter`** (§6.3) : ce graphique vit dans une
 * carte qui défile.
 *
 * ## Encodage — pourquoi ces couleurs-là
 *
 * Brut et lissé ne sont **pas deux identités** : c'est la même mesure, à deux
 * degrés de filtrage. Le brut porte donc la teinte de **mise en retrait** (un
 * gris de texte) et le lissé la teinte d'**accent** — et non deux couleurs
 * catégorielles, qui auraient laissé croire à deux grandeurs indépendantes.
 *
 * Valeurs mesurées contre le fond de carte, pas choisies à l'œil — **et une
 * série par thème depuis l'étape 10** :
 *
 *              thème sombre (`#1c1c24`)      thème clair (`#ffffff`)
 *   brut       `#9c9ca3`  → 6,20:1           `#6b6b75`  → 5,27:1
 *   lissé      `#3987e5`  → 4,65:1           `#106dce`  → 5,12:1
 *   objectif   `#c98500`  → 5,51:1           `#c28421`  → 3,17:1
 *
 * ⚠️ Reprendre les teintes du sombre en clair aurait laissé l'objectif à
 * **2,6:1** sur une carte blanche, sous le plancher de 3 exigé d'une marque.
 * Les valeurs claires sortent de la même recherche que la palette des macros,
 * jugée par le même validateur (détail dans `styles/tokens.css`).
 *
 * La paire colorée lissé ↔ objectif est vérifiée en « toutes paires » sous
 * simulation de daltonisme : ΔE 27,4 en protanopie, 30,7 en vision normale —
 * très au-dessus des planchers (8 et 15). Le gris, lui, ne fait pas d'identité
 * par la teinte : il s'en remet à sa graisse de trait et à la légende, qui est
 * **toujours présente**.
 *
 * ⚠️ La légende ne disparaît jamais, même quand la place manque : même règle que
 * les libellés des jauges de macros (`MacroChip`) — la couleur est un repère
 * secondaire, jamais le seul porteur du sens.
 */

import { useState } from 'react';

import type { WeightPointDTO } from '../../../../shared/types';
import { TOKENS } from '../../components/ui/tokens';
import { useElementWidth } from '../../hooks/useElementWidth';
import { ChartTooltip } from '../../components/ui/ChartTooltip';
import { useChartSweep } from '../../hooks/useChartSweep';
import { nearestIndex, niceTicks, toEpochDay, verticalWindow } from '../../lib/chartScale';
import { formatDayShort, formatKg } from './weightFields';

/* ==========================================================================
 * Constantes de dessin
 * ======================================================================= */

/**
 * Teintes des marques — **une valeur par thème**, résolue par le navigateur.
 *
 * 🔴 Elles sont posées en `style={{ stroke }}` / `style={{ fill }}` et **jamais**
 * en attribut de présentation SVG (`stroke="…"`). Une `var()` dans un attribut
 * de présentation n'est pas substituée de façon fiable ; dans une propriété de
 * style, elle l'est toujours. C'est la seule contrainte que la bascule des
 * jetons vers CSS ait imposée à ce fichier.
 *
 * Les mesures de contraste des deux thèmes sont en tête de `styles/tokens.css`.
 * En clair, la courbe lissée et l'objectif sont **recalculés** : repris tels
 * quels du sombre, l'objectif serait tombé à 2,6:1 sur une carte blanche.
 */
/*
 * 🔴 **Les quatre teintes du graphique viennent de la palette de la refonte, et
 * aucune ne lui est propre.** Le prototype ne dessine qu'un croquis à deux
 * séries et ne nomme pas de jetons de graphique ; en inventer aurait été
 * ajouter une palette parallèle pour un seul écran.
 *
 *   - **brut** : `--ink-soft`, 4,9:1 sur surface. Pas `--ink-faint`, qui tombe
 *     à 2,50:1 — un trait qui porte du sens exige 3:1 (WCAG 2.1 §1.4.11) ;
 *   - **lissée** : `--brand`, la courbe qu'on vient lire ;
 *   - **objectif** : `--ink`. Il est déjà **tireté**, donc distinct du brut par
 *     la forme autant que par la valeur — et lui donner une teinte de macro
 *     leur ferait dire deux choses (`design-tokens.md`, principe 2) ;
 *   - **grille** : `--line`, la même que tous les filets de l'application.
 */
const RAW_COLOR = 'var(--ink-soft)';
const SMOOTH_COLOR = 'var(--brand)';
const GOAL_COLOR = 'var(--ink)';

/** Grille et axes : traits pleins d'un cheveu, volontairement effacés. */
const GRID_COLOR = 'var(--line)';

const PLOT_HEIGHT = 168;
/**
 * Marges. `left` loge trois graduations en kg (« 78,5 »), `bottom` la bande de
 * dates — cette dernière est **dans** la hauteur totale, jamais rognée : un
 * conteneur qui n'inclut pas sa bande d'axe produit un mini-défilement interne.
 */
const MARGIN = { top: 10, right: 10, bottom: 18, left: 34 };

const TOTAL_HEIGHT = PLOT_HEIGHT + MARGIN.top + MARGIN.bottom;

/** En dessous de cette largeur, on ne dessine pas : rien n'est encore mesuré. */
const MIN_WIDTH = 80;

/* ==========================================================================
 * Échelles
 * ======================================================================= */

/*
 * ⚠️ **`toEpochDay`, `niceTicks` et l'ouverture de fenêtre vivent désormais
 * dans `lib/chartScale.ts`** *(29/08/2026)*, parce qu'un second graphique est
 * arrivé — la charge d'un exercice dans le temps. C'est ce que le V2 §6.3
 * annonçait : « trois primitives réutilisées dix fois, ce n'est pas dix
 * graphiques ».
 *
 * 🔴 **Rien du comportement de cet écran n'a changé** : les fonctions ont été
 * déplacées à l'identique, commentaires compris. Ce qui reste ici est ce qui
 * lui appartient — la fenêtre d'objectif, les quatre plages, les deux séries.
 */

interface Scale {
  x: (date: string) => number;
  y: (kg: number) => number;
  ticks: number[];
}

/**
 * Construit les échelles.
 *
 * ⚠️ **Un seul point, ou plusieurs points à la même valeur, ne doit pas produire
 * une échelle plate** : la division par `max − min` vaudrait zéro et tout le
 * tracé partirait à `NaN`. On ouvre alors la fenêtre d'un kilo de part et
 * d'autre, ce qui donne une ligne horizontale au milieu du cadre — la lecture
 * juste pour « une seule pesée ».
 *
 * ⚠️ **Une seule échelle verticale**, en kg, partagée par les trois marques. Un
 * second axe (par exemple la tendance en kg/semaine) inventerait une corrélation
 * que les données ne portent pas ; la tendance est affichée en chiffres, à part.
 */
function buildScale(
  points: readonly WeightPointDTO[],
  goalKg: number | null,
  width: number,
): Scale {
  const plotWidth = width - MARGIN.left - MARGIN.right;

  const days = points.map((point) => toEpochDay(point.date));
  const firstDay = Math.min(...days);
  const lastDay = Math.max(...days);
  const daySpan = lastDay - firstDay;

  const values = points.flatMap((point) => [point.kg, point.smoothed]);
  // L'objectif entre dans l'échelle **seulement s'il est proche** : un objectif
  // à 70 kg quand on en pèse 95 écraserait toute la variation réelle sur trois
  // pixels. Au-delà de 5 kg hors cadre, la ligne d'objectif n'est pas dessinée
  // et un libellé le dit — c'est traité par l'appelant via `goalInScale`.
  if (goalKg !== null) values.push(goalKg);

  const { min, max } = verticalWindow(values);

  const x = (date: string): number => {
    if (daySpan === 0) return MARGIN.left + plotWidth / 2;
    return MARGIN.left + ((toEpochDay(date) - firstDay) / daySpan) * plotWidth;
  };

  const y = (kg: number): number =>
    MARGIN.top + PLOT_HEIGHT - ((kg - min) / (max - min)) * PLOT_HEIGHT;

  return { x, y, ticks: niceTicks(min, max) };
}

/** Chemin SVG d'une série. */
function linePath(
  points: readonly WeightPointDTO[],
  scale: Scale,
  value: (point: WeightPointDTO) => number,
): string {
  return points
    .map((point, index) => {
      const command = index === 0 ? 'M' : 'L';
      return `${command}${scale.x(point.date).toFixed(1)} ${scale.y(value(point)).toFixed(1)}`;
    })
    .join(' ');
}

/* ==========================================================================
 * Légende
 * ======================================================================= */

function LegendKey({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: '0.7rem',
        // Le texte porte un jeton de texte, jamais la couleur de la série :
        // #c98500 en 11 px sur fond sombre serait au ras du seuil.
        color: TOKENS.textQuiet,
        whiteSpace: 'nowrap',
      }}
    >
      <svg width="14" height="8" aria-hidden focusable="false">
        <line
          x1="0"
          y1="4"
          x2="14"
          y2="4"
          style={{ stroke: color }}
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray={dashed === true ? '3 3' : undefined}
        />
      </svg>
      {label}
    </span>
  );
}

/* ==========================================================================
 * Composant
 * ======================================================================= */

export function WeightChart({
  points,
  goalKg,
}: {
  points: readonly WeightPointDTO[];
  goalKg: number | null;
}) {
  const { ref: hostRef, width } = useElementWidth();
  /**
   * La **date** du point désigné, `null` = on affiche le dernier.
   *
   * 🔴 **Une date, jamais un rang** *(05/09/2026)*. Le premier jet retenait
   * l'index, ce qui suffisait tant que la sélection mourait au relâchement.
   * Depuis qu'elle survit, elle traverse les changements de plage — et le rang
   * 12 de « 90 jours » n'est pas le rang 12 de « 30 jours ». On aurait désigné
   * une autre pesée, ou aucune, sans que rien ne le dise. C'est la leçon du
   * `clientId` transposée : **l'identité d'un point est sa donnée, pas sa
   * position.** Une date absente de la plage courante ne désigne simplement
   * plus rien, ce qui est le comportement juste.
   */
  const [activeDate, setActiveDate] = useState<string | null>(null);

  /*
   * L'objectif n'entre dans l'échelle que s'il reste dans le voisinage des
   * pesées. Sinon la variation réelle — quelques centaines de grammes — serait
   * écrasée par un écart de plusieurs kilos, et la courbe deviendrait une ligne
   * droite illisible.
   */
  const extremes = points.flatMap((point) => [point.kg, point.smoothed]);
  const goalInScale =
    goalKg !== null &&
    points.length > 0 &&
    goalKg >= Math.min(...extremes) - 5 &&
    goalKg <= Math.max(...extremes) + 5;

  const readable = width >= MIN_WIDTH && points.length > 0;
  const scale = readable ? buildScale(points, goalInScale ? goalKg : null, width) : null;

  /*
   * ⚠️ **Dérivé au rendu, jamais remis à zéro dans un effet** : c'est la
   * discipline du pager (« dérivé au rendu, pas posé dans un effet »), et
   * `react-hooks/set-state-in-effect` refuserait l'autre forme.
   */
  const activeIndex = (() => {
    if (activeDate === null) return null;
    const index = points.findIndex((point) => point.date === activeDate);
    return index === -1 ? null : index;
  })();
  const designe = activeIndex === null ? null : (points[activeIndex] ?? null);
  const shown = designe ?? points.at(-1);

  /**
   * Le pointeur désigne une **date**, jamais un trait de 2 px : on cherche le
   * point dont l'abscisse est la plus proche (`nearestIndex`, partagé avec la
   * courbe d'un exercice depuis le 05/09/2026). C'est ce qui rend le geste
   * utilisable au doigt sur 393 px, où viser un point serait illusoire.
   *
   * ⚠️ `touch-action: pan-y` : le défilement vertical reste natif — donc fluide —
   * et seul l'horizontal nous revient. Même mécanique que la piste du carrousel
   * de journées (`.day-track`) ; en `none`, l'écran ne défilerait plus au doigt
   * dès qu'on le touche par le graphique.
   *
   * 🔴 **La sélection SURVIT au relâchement depuis le 05/09/2026**, et c'est ce
   * que « je clique sur un point » demandait vraiment. Elle était remise à
   * `null` au `pointerup` : sur iPhone, une tape brève affichait donc la valeur
   * le temps du contact — c'est-à-dire le temps où le doigt la cache. Le
   * balayage, lui, est intact : on peut toujours faire glisser pour parcourir
   * la courbe, on ne perd simplement plus le point en levant le doigt.
   *
   * ⚠️ **Le retrait ne se fait qu'au `pointerdown`** (`toggle`), jamais pendant
   * un glissement : sinon, traverser deux fois le même point pendant le
   * balayage l'allumerait et l'éteindrait sous les doigts.
   */
  const { attacher, handlers } = useChartSweep((pointerX, _y, toggle) => {
    if (!scale || points.length === 0) return;
    const found = nearestIndex(
      points.map((point) => scale.x(point.date)),
      pointerX,
    );
    const date = found === null ? null : (points[found]?.date ?? null);
    if (date === null) return;
    setActiveDate((current) => (toggle && current === date ? null : date));
  });

  /*
   * Aucune pesée : on n'affiche **ni lecture ni légende**.
   *
   * ⚠️ Constaté en relisant la capture de l'écran vide : une légende qui nomme
   * « Poids brut » et « Moyenne 7 j » au-dessus d'un cadre vide annonce deux
   * courbes qui n'existent pas, et la ligne de lecture affichait « — · aucune
   * pesée » juste au-dessus du message qui dit déjà la même chose. La règle
   * « légende toujours présente » vaut à partir de deux séries **tracées** ;
   * ici il n'y en a aucune.
   */
  if (points.length === 0) {
    return (
      <div
        style={{
          height: `${String(TOTAL_HEIGHT)}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.85rem',
          color: TOKENS.textQuiet,
          textAlign: 'center',
        }}
      >
        Aucune pesée sur cette période.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {/*
        Lecture **au-dessus** du tracé, à place fixe. Elle affiche le dernier
        point tant qu'aucun n'est désigné : l'information est donc là sans aucun
        geste, et désigner un point ne fait que la déplacer.

        ⚠️ **Elle ne remplace plus l'infobulle, elle la double** *(05/09/2026)*.
        Ce commentaire disait « plutôt qu'une infobulle flottante », au motif
        qu'une bulle près du doigt sort du cadre près d'un bord. L'objection
        était juste et a été **traitée** plutôt qu'esquivée (`ChartTooltip`, qui
        ancre selon le tiers) : ce que la ligne fixe ne pouvait pas faire, c'est
        désigner — sur un graphique, le lien entre le nombre et le point est
        précisément ce qu'on vient chercher.

        ⚠️ **Elle est CONSERVÉE malgré la bulle**, et ce n'est pas un doublon
        par distraction : elle est la lecture **sans geste**, à place fixe, et
        elle survit à un doigt qui masque la courbe. La bulle, elle, ne dit
        qu'une chose que la ligne ne dit pas — *lequel* de ces points.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '0.5rem',
          minHeight: '1.25rem',
          fontSize: '0.78rem',
          fontVariantNumeric: 'tabular-nums',
          color: TOKENS.textQuiet,
        }}
      >
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {shown ? formatDayShort(shown.date) : '—'}
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>
          {shown ? (
            <>
              <span style={{ color: TOKENS.text }}>{formatKg(shown.kg)}</span> kg · lissé{' '}
              <span style={{ color: TOKENS.text }}>{formatKg(shown.smoothed)}</span>
            </>
          ) : (
            'aucune pesée'
          )}
        </span>
      </div>

      <div ref={hostRef} className="n-chart-host">
        {readable && scale ? (
          <svg
            width={width}
            height={TOTAL_HEIGHT}
            role="img"
            aria-label={`Évolution du poids sur ${String(points.length)} pesée(s)`}
            style={{ display: 'block', touchAction: 'pan-y' }}
            ref={attacher}
            {...handlers}
          >
            {/*
              🔴 **Le trait vertical du point désigné** *(06/09/2026, demandé
              après usage)*. Il compte plus ici qu'ailleurs : deux courbes se
              superposent — le brut et le lissé — et sans repère commun, on ne
              sait pas laquelle des deux valeurs de la ligne de lecture
              correspond à quel rond.

              ⚠️ **Sous la grille et sous les courbes** : un repère qui passe
              par-dessus la mesure la masque au moment où on la regarde.
              ⚠️ Posé sur `designe` et non sur `shown` : `shown` retombe sur la
              dernière pesée quand rien n'est désigné, et un trait planté en
              permanence au bord droit se lirait comme un seuil.
            */}
            {designe && (
              <line
                x1={scale.x(designe.date)}
                x2={scale.x(designe.date)}
                y1={MARGIN.top}
                y2={MARGIN.top + PLOT_HEIGHT}
                className="n-chart__crosshair"
              />
            )}

            {/* Grille : traits pleins, jamais pointillés — un pointillé se lit
                comme un seuil, et le seuil ici c'est l'objectif. */}
            {scale.ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={MARGIN.left}
                  y1={scale.y(tick)}
                  x2={width - MARGIN.right}
                  y2={scale.y(tick)}
                  style={{ stroke: GRID_COLOR }}
                  strokeWidth={1}
                />
                <text
                  x={MARGIN.left - 5}
                  y={scale.y(tick) + 3}
                  textAnchor="end"
                  fontSize={9}
                  style={{ fill: TOKENS.textQuiet, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatKg(tick)}
                </text>
              </g>
            ))}

            {/* Objectif : ligne de référence. Pointillée **à dessein** — c'est
                le seul trait du graphique qui n'est pas une mesure. */}
            {goalInScale && goalKg !== null && (
              <line
                x1={MARGIN.left}
                y1={scale.y(goalKg)}
                x2={width - MARGIN.right}
                y2={scale.y(goalKg)}
                style={{ stroke: GOAL_COLOR }}
                strokeWidth={1.5}
                strokeDasharray="4 4"
              />
            )}

            {/* Brut : trait fin, en retrait. */}
            <path
              d={linePath(points, scale, (point) => point.kg)}
              fill="none"
              style={{ stroke: RAW_COLOR }}
              strokeWidth={1.25}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {/* Lissé : la série qu'on vient lire, donc la plus lourde. */}
            <path
              d={linePath(points, scale, (point) => point.smoothed)}
              fill="none"
              style={{ stroke: SMOOTH_COLOR }}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {/* Points bruts : seulement quand ils ne se chevauchent pas. Au-delà,
                ils formeraient un ruban plein qui masquerait la courbe. */}
            {points.length <= 45 &&
              points.map((point) => (
                <circle
                  key={point.date}
                  cx={scale.x(point.date)}
                  cy={scale.y(point.kg)}
                  r={2}
                  style={{ fill: RAW_COLOR }}
                />
              ))}

            {/* Point désigné : anneau de la couleur du fond, pour rester lisible
                là où il croise une ligne. */}
            {shown && (
              <circle
                cx={scale.x(shown.date)}
                cy={scale.y(shown.smoothed)}
                r={4}
                style={{ fill: SMOOTH_COLOR, stroke: TOKENS.surfaceRaised }}
                strokeWidth={2}
              />
            )}

            {/* Bande de dates : deux repères seulement — début et fin. Avec des
                dates au milieu, elles se chevauchent dès la plage « 1 an ». */}
            <text
              x={MARGIN.left}
              y={TOTAL_HEIGHT - 5}
              textAnchor="start"
              fontSize={9}
              style={{ fill: TOKENS.textQuiet }}
            >
              {formatDayShort(points[0]?.date ?? '')}
            </text>
            {points.length > 1 && (
              <text
                x={width - MARGIN.right}
                y={TOTAL_HEIGHT - 5}
                textAnchor="end"
                fontSize={9}
                style={{ fill: TOKENS.textQuiet }}
              >
                {formatDayShort(points.at(-1)?.date ?? '')}
              </text>
            )}
          </svg>
        ) : (
          <div
            style={{
              height: `${String(TOTAL_HEIGHT)}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.85rem',
              color: TOKENS.textQuiet,
              textAlign: 'center',
            }}
          >
            {/*
              Largeur pas encore mesurée (premier rendu). On réserve la hauteur
              exacte du graphique plutôt que d'afficher un message : le cas dure
              une frame, et une boîte vide de la bonne taille évite le saut de
              mise en page qu'un texte plus court provoquerait.
            */}
          </div>
        )}

        {/*
          🔴 **L'infobulle, ancrée au point désigné** *(05/09/2026, demandé
          après usage)*. Elle est posée **hors du SVG**, en HTML : du texte SVG
          ne sait ni revenir à la ligne, ni porter un fond arrondi sans qu'on
          dessine soi-même le rectangle et qu'on mesure la chaîne pour le
          dimensionner. Le recadrage — la seule objection sérieuse de l'étape 8
          — vit dans `ChartTooltip`, une fois pour les deux graphiques.

          ⚠️ **Ancrée sur le point LISSÉ**, là où est l'anneau : la bulle doit
          désigner ce qu'on voit désigné, sinon elle flotte à côté d'un repère
          qui, lui, ne bouge pas.
        */}
        {designe && scale && (
          <ChartTooltip
            x={scale.x(designe.date)}
            y={scale.y(designe.smoothed)}
            boxWidth={width}
            boxHeight={TOTAL_HEIGHT}
          >
            {formatDayShort(designe.date)}
            <br />
            {formatKg(designe.kg)} kg · lissé {formatKg(designe.smoothed)}
          </ChartTooltip>
        )}
      </div>

      {/* Légende — **toujours présente** dès qu'il y a deux séries. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
        <LegendKey color={RAW_COLOR} label="Poids brut" />
        <LegendKey color={SMOOTH_COLOR} label="Moyenne 7 j" />
        {goalKg !== null && (
          /*
            La valeur est **dans la clé de légende**, pas au bout de la ligne
            pointillée. Une étiquette posée sur le tracé se heurterait au dernier
            point ou au bord droit selon les données ; ici elle ne peut, par
            construction, chevaucher personne. Sans elle, le lecteur voyait un
            trait pointillé sans savoir à quel poids il correspond — la valeur
            n'était que dans la carte du dessous.
          */
          <LegendKey
            color={GOAL_COLOR}
            dashed
            label={`Objectif ${formatKg(goalKg)} kg${goalInScale ? '' : ' (hors cadre)'}`}
          />
        )}
      </div>
    </div>
  );
}
